/**
 * Non-visual helpers for the global search page: the search call itself, the Prices API and
 * delivery-time line builders, paging, the bulk UOM options, URL query reading and two small
 * hooks.
 *
 * Position: used by `Search.tsx`, `Context/SearchContext.tsx`, `ListView/ProductSection.tsx`,
 * `TableView/` and `Helpers/` under `components/Search/`. Outside search,
 * `Checkout/OrderReview/Context/OrderReviewContext.tsx` and
 * `Checkout/ExpressCheckout/Context/ExpressCheckoutContext.tsx` use {@link usePrevious}, and
 * `EasyQuote/Helper/EasyQuoteSummaryBox/PageSupport/SummaryBox.functions.ts` uses
 * {@link makeUOMplural}.
 *
 * Rules a change must keep:
 * - A result's `index` is its 0-based position in the combined list (recently purchased first,
 *   then matching products) set by {@link getSearchResults}. Price and delivery-time lines use
 *   `index + 1` as `itemNumber` / `id` ({@link getItemsForPricingRequestBody}), and
 *   `Context/SearchContext.tsx` matches responses back by SKU and that number.
 *
 * Not referenced: {@link getRequestItemsForPricing} and {@link getOptions}, and
 * {@link OptionType}, which only `getOptions` uses.
 */
import React from "react";
import {
	GlobalSearchResultsResponse,
	ProductsListState,
	DeliveryTimesRequestItems,
	PricingRequestType,
	UomDefaultEnum,
} from "@components/Models/GlobalSearchResults.Models";
import { Option } from "@ryerson/frontend.form-controls";
import {
	getConversionUomObject,
	getCustomerPartNumber,
	Items,
	Processing,
	updatedQuantityBasedOnQuantityUom,
} from "@ryerson/frontend.common";
import { isCallNeedProcessingWithValidQuantity } from "@components/Helper/pricingResponseHelpers";
import { convertQuantityUOM } from "@ryerson/common.conversions";
import { UOM } from "@ryerson/common.enums";
import { AxiosInstance } from "axios";
import { UOMDisplay, UOMValue } from "@ryerson/common.uom";
import { ShoppingListProduct } from "@components/Helper/shoppingListHelper";
import { FormUnitOfMeasure, MatchingProduct } from "@ryerson/frontend.common";
import { APIUrl } from "@enums/apiUrl.enums";
import { UserProfile } from "@ryerson/frontend.application";
import { isRegisterUser } from "@components/Helper/common";
import { AxiosResponse, AxiosError } from "axios";
import { IError } from "@ryerson/common.models";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { UpdatedMatchingProduct } from "./Search.models";

/**
 * @des - Returns copies of the products in which every blank attribute value (`isNullData`) is
 * replaced with "N/A", for display. The input array and its objects are not changed.
 * */

//export const DASH = "\u2014";

export const modAttributesData = (responseData: MatchingProduct[]): MatchingProduct[] => {
	if (responseData.length === 0) return [];
	const modProdList: MatchingProduct[] = responseData?.map((product) => {
		const modAttributes = product?.attributes?.map((attribute) => {
			const { value = null } = attribute;
			return isNullData(value) ? { ...attribute, value: "N/A" } : attribute;
		});
		return { ...product, attributes: modAttributes };
	});
	return modProdList;
};

/** @des Utility to check for null values  */
const isNullData = (value: any): boolean => {
	const nullValues = ["N/A", "na", "NA", "n/a", null, ""];
	return !!(typeof value === "undefined" || nullValues.includes(value as any));
};

// -----------------------------
// Options for Paging Component
// -----------------------------
/** Page sizes offered by the search list, table and tab views. The context starts at 20. */
export const pageSizeOptions: Option[] = [
	{
		display: "10",
		value: "10",
	},
	{
		display: "20",
		value: "20",
	},
	{
		display: "30",
		value: "30",
	},
	{
		display: "50",
		value: "50",
	},
];

/***
 * @des  Gets subset of Array of Data to show for each page. `page` is 1-based.
 * **/

export const getDataPageSlice = <T,>(page: number, pageSize: number, data: T[]) => {
	const dataLength = data?.length;
	const dataStartSlice = page * pageSize - pageSize;

	const dataEndSlice = Math.min(
		dataStartSlice + pageSize,
		dataStartSlice + Math.abs(dataLength - dataStartSlice)
	);

	return data?.slice(dataStartSlice, dataEndSlice);
};

// AJAX API CALL for SEARCH RESULTS
/**
 * Runs a global search: `GET APIUrl.GLOBAL_SEARCH_V2` (Items API, `web-items-api`) with
 * `result-type=detailed`. The phrase is put into the URL as typed, without URL encoding. A
 * `zip-code` is added only for a user without a customer account (`isRegisterUser` false).
 *
 * On success, `matchingProducts` is replaced by recently purchased products followed by the
 * matching products, each given its 0-based `index` in that list; `setSearchResults` gets the
 * response with that list. An error response or a non-200 status clears the results; a rejected
 * request leaves them as they were. Errors are logged to the console, and `setSearchTrigger` is
 * set back to false in every case.
 */
export const getSearchResults = async (
	displaySearchText: string,
	setSearchTrigger: React.Dispatch<React.SetStateAction<boolean>>,
	setSearchResults: React.Dispatch<React.SetStateAction<GlobalSearchResultsResponse | undefined>>,
	axiosInstance: AxiosInstance,
	profile: UserProfile,
	zipCode: string
) => {
	let url = `${APIUrl.GLOBAL_SEARCH_V2}?search-phrase=${displaySearchText}&result-type=detailed`;
	if (!isRegisterUser(profile) && zipCode?.length) {
		url = `${url}&zip-code=${zipCode}`;
	}

	try {
		const response = await axiosInstance.get<
			null,
			AxiosResponse<GlobalSearchResultsResponse> | AxiosError<IError>
		>(url);
		if (isAxiosError(response) || response.status !== 200) {
			setSearchResults(undefined);
			const errorMessage = isAxiosError(response)
				? response.response?.data?.message || response.message
				: `Search request failed with status ${response.status}`;
			throw new Error(errorMessage);
		}
		const { data } = response;
		if (data) {
			const matchingProducts =
				data.recentlyPurchased.length > 0
					? data.recentlyPurchased.concat(data.matchingProducts)
					: data.matchingProducts;

			const updatedMatchingProducts = matchingProducts.map((product, index) => ({
				...product,
				index: index,
			}));

			setSearchResults({
				...data,
				matchingProducts: updatedMatchingProducts,
			});
		}
		setSearchTrigger(false);
	} catch (error) {
		setSearchTrigger(false);
		console.error(error);
	}
};

/** @desc - Create Items Array for Pricing Call. Not referenced. */
export const getRequestItemsForPricing = (
	currentState: ProductsListState,
	quantity: number,
	qtyUOM: UOM,
	priceUOM: UOM,
	totalWeight: number,
	zipcode: string
) => {
	return {
		type: PricingRequestType.Default,
		zipCode: zipcode,
		items: [
			{
				materialSku: currentState?.materialMaster,
				itemNumber: currentState?.index,
				quantity: quantity,
				quantityUom: qtyUOM,
				pricingUom: priceUOM,
				weight: totalWeight,
			},
		],
	};
};

/**
 * @desc - Create Items Array for Pricing Call
 *
 * One Prices API line per product whose row (matched by `index` and SKU) can be priced now
 * (`Helper/pricingResponseHelpers.ts` → `isCallNeedProcessingWithValidQuantity`). A row at
 * quantity 0 is priced as quantity 1 with the product's default weight; otherwise the weight is
 * the quantity converted to pounds. `itemNumber` is the row's `index + 1`. The row's cut is sent
 * as `processing` only when it has fields. Also the source of the delivery-time lines.
 */
export const getItemsForPricingRequestBody = (
	productListItems: MatchingProduct[],
	productState: ProductsListState[]
): Items[] => {
	let requestDetails: Items[] = [];
	productListItems.forEach((product, productIndex) => {
		const initialStateOfProduct = productState.find(
			(stateItem) =>
				stateItem.index === (product as UpdatedMatchingProduct).index &&
				stateItem.materialMaster === product.materialMaster
		);
		if (
			initialStateOfProduct &&
			isCallNeedProcessingWithValidQuantity(
				product,
				initialStateOfProduct?.definedCut,
				initialStateOfProduct.quantity
			)
		) {
			let totalWeight: number = initialStateOfProduct.defaultWeight;
			const updatedQuantity =
				initialStateOfProduct?.quantity === 0 ? 1 : initialStateOfProduct?.quantity;

			if (initialStateOfProduct?.quantity > 0) {
				let conversionObject = getConversionUomObject(
					updatedQuantity,
					initialStateOfProduct.qtyUom as UOM,
					initialStateOfProduct.priceUom as UOM,
					product
				);
				totalWeight = Number(
					Number(convertQuantityUOM(conversionObject, UOM.LB)).toFixed(2)
				);
			}

			const itemsBody: Items = {
				materialSku: product?.materialMaster,
				itemNumber: initialStateOfProduct.index + 1,
				comboKey: product?.comboKey || "",
				quantity: updatedQuantity,
				quantityUom: initialStateOfProduct.qtyUom as UOM,
				pricingUom: initialStateOfProduct.priceUom as UOM,
				customerPartNumber: getCustomerPartNumber(product?.altPartNumber),
				weight: totalWeight,
				...(initialStateOfProduct.definedCut &&
					Object.keys(initialStateOfProduct.definedCut).length > 0 && {
						processing: initialStateOfProduct.definedCut,
					}),
			};
			requestDetails.push(itemsBody);
		}
	});
	return requestDetails;
};

/** @desc - Maps chunks of pricing lines to one flat list of delivery-times request lines. */
export const deliveryTimesItemsForRequest = (
	chunkOfArray: Items[][]
): DeliveryTimesRequestItems[] =>
	chunkOfArray.flat().map((item) => {
		return {
			id: item.itemNumber,
			materialMaster: item.materialSku,
			priceUOM: item.pricingUom,
			comboKey: item.comboKey,
			qty: item.quantity,
			qtyUOM: item.quantityUom,
			processing: item?.processing,
			customerPartNo: item?.customerPartNumber,
		} as DeliveryTimesRequestItems;
	});

/**
 * Reads `page` or `view` from the URL query. Only finds them at the start of the query string,
 * as `page=…`, `view=…` or `page=…&view=…`; anything else returns "".
 */
export const getQueryStringParam = (qString: "page" | "view") => {
	const windowQuerystring = (window as any)?.location.search;
	const regEx = /\b(page=([0-9]+))?((&)?view=([A-Za-z]+))?/;
	const response = windowQuerystring.match(regEx);

	if (!response) return "";

	// response[2] is the page value, response[5] is the view value
	if (qString === "page") {
		return response[2] || "";
	} else if (qString === "view") {
		return response[5] || "";
	}
	return "";
};

/** "PC" → "PCS" and "LB" → "LBS", for display keys in `UOMDisplay`; other UOMs unchanged. */
export const makeUOMplural = (uom: any) => (uom === "PC" ? "PCS" : uom === "LB" ? "LBS" : uom);

/** Flags of a product UOM entry to filter on in {@link getOptions}. Not referenced elsewhere. */
export enum OptionType {
	isQuantity = "isQuantity",
	isPricing = "isPricing",
	isQuantityDefault = "isQuantityDefault",
	isPricingDefault = "isPricingDefault",
}

/**
 * Dropdown options from a product's UOMs that have the given flag; pounds when the product has
 * no UOM list. Not referenced.
 */
export const getOptions = (productItem: MatchingProduct, optionType: OptionType): Option[] => {
	const defaultOption: Option = { display: UOMDisplay.LBS, value: UOMValue.LB };

	const modFunction = (uom: any) => (optionType.includes("Quantity") ? makeUOMplural(uom) : uom);
	const qtyUomOption = productItem.unitOfMeasure
		?.filter((measure: FormUnitOfMeasure) => measure[optionType])
		.map((measure: FormUnitOfMeasure): Option => {
			return {
				display: UOMDisplay[modFunction(measure.unitOfMeasure) as keyof typeof UOMDisplay],
				value: UOMValue[measure.unitOfMeasure as keyof typeof UOMValue],
			};
		}) || [defaultOption];

	return qtyUomOption;
};

/** The value from the previous render (`undefined` on the first). */
export function usePrevious(value: any) {
	const ref = React.useRef();
	React.useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
}

/**
 * The first value seen, kept for the component's lifetime (`undefined` during the first
 * render). Used by `ListView/ProductSection.tsx` to tell which row values the user changed.
 */
export function useOriginal(value: any) {
	const ref = React.useRef();
	const [times, setTimes] = React.useState(0);
	React.useEffect(() => {
		!times && (ref.current = value);
		setTimes((prev) => prev + 1);
	}, [value]);

	return ref.current;
}

interface ProdToShopListProps {
	productItem: MatchingProduct;
	quantity: number;
	qtyUOM: string;
	priceUOM: string;
	cutProcessing?: Processing;
}
/**
 * One search result as a one-item list for the Shopping API's add-to-list body
 * (`Helper/shoppingListHelper.ts` → `ShoppingListProduct`); `[]` without a product. The cut is
 * included as `processing` only when its `length` (the cut length) is set and non-zero.
 */
export const productItemToShoppingListItem = ({
	productItem,
	quantity,
	qtyUOM,
	priceUOM,
	cutProcessing,
}: ProdToShopListProps) => {
	let shoppingListItem: ShoppingListProduct;

	if (productItem) {
		shoppingListItem = {
			ryersonSku: productItem.materialMaster,
			customerPartNumber: getCustomerPartNumber(productItem?.altPartNumber),
			quantity: quantity,
			quantityUom: qtyUOM,
			pricingUom: priceUOM,
			customerRefNumber: null,
			purchaseOrderNumber: null,
			notes: null,
			domesticRequired: false,
			cutFlag: productItem.cutFlag,
			...(cutProcessing?.length && { processing: cutProcessing }),
		};

		return [shoppingListItem];
	} else return [];
};

interface Enum {
	[id: number]: string;
}
/** The key of the first enum member whose value is `value`, or `undefined`. */
export const enumKeyByValue = (aEnum: Enum, value: string) => {
	let val = Object.entries(aEnum).filter(([key, val]) => val === value)[0];
	let key = (val && val[0]) || undefined;

	return key;
};

/**
 * Quantity UOMs that every product in the list offers, as dropdown options, for the bulk
 * "default quantity UOM" control. `[]` when there are none in common. A one-product list
 * returns all of that product's UOMs, quantity or not, and an empty list throws (`reduce`
 * without a start value); `Search.tsx` calls it only when there are products.
 */
export const getBulkQuantityOptions = (productItems: any[]) => {
	return (
		productItems
			?.reduce((previous: MatchingProduct, current: MatchingProduct) => {
				const qtyUomOption = previous.unitOfMeasure?.filter(
					(measure: FormUnitOfMeasure) =>
						measure.isQuantity &&
						current?.unitOfMeasure
							?.filter((curMeasure: FormUnitOfMeasure) => curMeasure.isQuantity)
							?.map((filteredCurMeasure) => filteredCurMeasure.unitOfMeasure)
							?.includes(measure.unitOfMeasure)
				);
				return { ...previous, unitOfMeasure: qtyUomOption };
			})
			.unitOfMeasure?.map((measure: FormUnitOfMeasure) => {
				return {
					display: measure.unitOfMeasure.toLowerCase(),
					value: measure.unitOfMeasure,
				};
			}) || []
	);
};

/** Pricing UOMs every product offers; same edge cases as {@link getBulkQuantityOptions}. */
export const getBulkPricingUomOptions = (productItems: any[]) => {
	return (
		productItems
			?.reduce((previous: MatchingProduct, current: MatchingProduct) => {
				const priceUomOption = previous.unitOfMeasure?.filter(
					(measure: FormUnitOfMeasure) =>
						measure.isPricing &&
						current?.unitOfMeasure
							?.filter((curMeasure: FormUnitOfMeasure) => curMeasure.isPricing)
							?.map((filteredCurMeasure) => filteredCurMeasure.unitOfMeasure)
							?.includes(measure.unitOfMeasure)
				);
				return { ...previous, unitOfMeasure: priceUomOption };
			})
			.unitOfMeasure?.map((measure: FormUnitOfMeasure) => {
				return {
					display: measure.unitOfMeasure.toLowerCase(),
					value: measure.unitOfMeasure,
				};
			}) || []
	);
};

/**
 * Applies a bulk quantity or pricing UOM to every row. A quantity UOM change also converts the
 * row's quantity to the new UOM. Rows with a defined cut keep their UOMs and quantity.
 */
export const updateProductsListsUoms = (
	uomValue: UOM,
	uomType: UomDefaultEnum,
	productsListState: ProductsListState[],
	matchingProducts: MatchingProduct[]
): ProductsListState[] => {
	return productsListState.map((curProductState) => {
		let currentMatchingItem = matchingProducts.find((matchingProduct: MatchingProduct) => {
			return matchingProduct.materialMaster === curProductState.materialMaster;
		});

		let updatedQuantity = curProductState.quantity;

		if (uomType === UomDefaultEnum.Quantity && currentMatchingItem) {
			updatedQuantity = updatedQuantityBasedOnQuantityUom(
				currentMatchingItem,
				uomValue,
				curProductState.quantity
			);
		}
		const definedCutExists = Object.keys(curProductState?.definedCut || {}).length > 0;
		if (definedCutExists) {
			return {
				...curProductState,
			};
		} else {
			return {
				...curProductState,
				[uomType === UomDefaultEnum.Quantity ? "qtyUom" : "priceUom"]: uomValue,
				quantity: updatedQuantity,
			};
		}
	});
};

//@desc  creates a MAP object of all possible attributes in data set, used in table view attributed config section
export const getAttributeMap = (list: MatchingProduct[]) => {
	let attMap = new Map();

	list.forEach((item: MatchingProduct) => {
		item.attributes?.forEach((attribute: any) => {
			attMap.set(attribute.name, attribute.value);
		});
	});
	return attMap;
};
